import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Download, Search } from '@mui/icons-material';
import { motion } from 'framer-motion';

import { db } from '../firebase';
import { checkAdminClaim } from '../utils/adminUtils';
import { glassStyles, colors } from '../theme';

const safeText = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
};

const getTimestampLabel = (timestamp) => {
  if (!timestamp) return 'No timestamp';

  try {
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleString();
    }

    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? 'No timestamp' : date.toLocaleString();
  } catch (err) {
    return 'No timestamp';
  }
};

const Admin = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const auth = getAuth();

  // Auth check with Firebase custom claims
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/signin');
        return;
      }

      // Check admin claim from ID token
      const isAdmin = await checkAdminClaim(currentUser);
      if (!isAdmin) {
        navigate('/');
        return;
      }

      setUser(currentUser);
      setLoading(false);
    });
    return unsubscribe;
  }, [auth, navigate]);

  // Fetch conversations
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'conversations'),
      orderBy('timestamp', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setConversations(data);
      },
      (err) => {
        console.error('Error fetching conversations:', err);
        setError(err.message);
      }
    );

    return unsubscribe;
  }, [user]);

  const filteredConversations = conversations.filter((conv) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      safeText(conv.userMessage?.content).toLowerCase().includes(search) ||
      safeText(conv.botResponse?.content).toLowerCase().includes(search) ||
      safeText(conv.content).toLowerCase().includes(search) ||
      safeText(conv.username).toLowerCase().includes(search) ||
      safeText(conv.userEmail).toLowerCase().includes(search)
    );
  });

  const exportData = () => {
    const dataStr = JSON.stringify(filteredConversations, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conversations_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 2 } }}>
      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <Box sx={{ ...glassStyles.card, p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' },
              }}
            >
              Admin Panel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage conversations
            </Typography>
          </Box>

          {/* Controls */}
          <Box
            sx={{
              ...glassStyles.card,
              p: { xs: 2, sm: 3 },
              mb: 3,
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <TextField
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.muted' }} /> }}
              sx={{ flexGrow: 1, minWidth: { xs: 120, sm: 200 } }}
            />
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={exportData}
              sx={{
                borderColor: 'rgba(255,255,255,0.2)',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
              }}
            >
              Export
            </Button>
            <Chip
              label={`${filteredConversations.length}`}
              size="small"
              sx={{ display: { xs: 'none', sm: 'flex' } }}
            />
          </Box>

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Conversations list */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredConversations.length === 0 ? (
              <Box sx={{ ...glassStyles.card, p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {searchTerm ? 'No conversations match your search' : 'No conversations yet'}
                </Typography>
              </Box>
            ) : (
              filteredConversations.map((conv, index) => (
                <motion.div
                  key={conv.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                >
                  <Box sx={{ ...glassStyles.subtle, p: 3 }}>
                    {/* Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 2,
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {safeText(conv.username, 'Anonymous')}
                      </Typography>
                      <Typography variant="body2" color="text.muted">
                        {getTimestampLabel(conv.timestamp)}
                      </Typography>
                    </Box>

                    {/* User message */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.muted" sx={{ mb: 0.5 }}>
                        User:
                      </Typography>
                      <Typography variant="body1">
                        {safeText(conv.userMessage?.content) || safeText(conv.content) || 'No message'}
                      </Typography>
                    </Box>

                    {/* Bot response */}
                    {conv.botResponse && (
                      <Box
                        sx={{
                          background: `${colors.primary.main}15`,
                          borderRadius: 2,
                          p: 2,
                          borderLeft: `3px solid ${colors.primary.main}`,
                        }}
                      >
                        <Typography variant="body2" color={colors.primary.light} sx={{ mb: 0.5 }}>
                          Nick:
                        </Typography>
                        <Typography variant="body1">
                          {safeText(conv.botResponse?.content)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </motion.div>
              ))
            )}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Admin;
